import React, { useEffect, useState, useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGaugeHigh,
  faCircleCheck,
  faPhone,
  faIdCard,
  faHashtag,
  faHeadset,
} from '@fortawesome/free-solid-svg-icons';
import { getMyInfo, getUserBalance, fetchTemplateCounts } from '../../../lib/api';
import '../styles/dashboard.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const chartOptions = {
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: {
    x: { display: false },
    y: { display: false },
  },
};

const miniChartData = {
  labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
  datasets: [{
    data: [65, 59, 80, 81, 56, 55],
    backgroundColor: 'rgba(255, 255, 255, 0.25)',
    borderColor: '#fff',
    borderWidth: 1.5,
    fill: true,
    tension: 0.3,
  }],
};

const StatCard = ({ title, value, trend, chartType }) => {
  const chart = useMemo(() => {
    if (chartType === 'doughnut') {
      return (
        <Doughnut
          data={{
            labels: [],
            datasets: [{
              data: [75, 25],
              backgroundColor: ['rgba(255,255,255,0.9)', 'rgba(255,255,255,0.2)'],
              borderWidth: 0,
            }],
          }}
          options={{ ...chartOptions, cutout: '70%' }}
        />
      );
    }
    return <Line data={miniChartData} options={chartOptions} />;
  }, [chartType]);

  return (
    <div className="dashboard-stat-card">
      <div className="dashboard-stat-content">
        <div className="dashboard-stat-text">
          <p className="dashboard-stat-title">{title}</p>
          <h2 className="dashboard-stat-value">{value}</h2>
          <p className="dashboard-stat-trend">{trend}</p>
        </div>
        <div className="dashboard-stat-chart">{chart}</div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const [myInfo, setMyInfo] = useState(null);
  const [balance, setBalance] = useState(null);
  const [templateCounts, setTemplateCounts] = useState([]);
  const [templateFilter, setTemplateFilter] = useState('Overall');
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [loadingCounts, setLoadingCounts] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getMyInfo()
      .then((data) => setMyInfo(data))
      .catch(() => setError('Failed to load account info'))
      .finally(() => setLoadingInfo(false));
    getUserBalance()
      .then((data) => setBalance(data?.balance ?? null))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchTemplateCounts()
      .then((data) => setTemplateCounts(Array.isArray(data) ? data : []))
      .catch(() => setTemplateCounts([]))
      .finally(() => setLoadingCounts(false));
  }, []);

  const filteredTemplateCounts = useMemo(() => {
    if (templateFilter === 'Overall') return templateCounts;
    return templateCounts.filter((c) => c.time_range === templateFilter);
  }, [templateCounts, templateFilter]);

  const lineChartData = useMemo(() => ({
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [{
      label: 'Revenue',
      data: [50, 75, 150, 100, 200, 175],
      backgroundColor: 'rgba(0, 168, 132, 0.2)',
      borderColor: 'var(--accent, #00a884)',
      borderWidth: 2,
      fill: true,
      tension: 0.35,
    }],
  }), []);

  const doughnutData = useMemo(() => ({
    labels: ['Completed', 'Remaining'],
    datasets: [{
      data: [75, 25],
      backgroundColor: ['var(--accent, #00a884)', 'rgba(0, 168, 132, 0.25)'],
      borderWidth: 0,
    }],
  }), []);

  const barChartData = useMemo(() => ({
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul'],
    datasets: [{
      label: 'Monthly Revenue',
      data: [3000, 4000, 5000, 4000, 6000, 7000, 8000],
      backgroundColor: 'var(--accent, #00a884)',
      borderRadius: 6,
      borderWidth: 0,
    }],
  }), []);

  const usersLineData = useMemo(() => ({
    labels: ['January', 'February', 'March', 'April', 'May', 'June', 'July'],
    datasets: [{
      label: 'Users Growth',
      data: [50, 75, 100, 120, 150, 200, 250],
      fill: true,
      backgroundColor: 'rgba(0, 168, 132, 0.15)',
      borderColor: 'var(--accent, #00a884)',
      borderWidth: 2,
      tension: 0.4,
    }],
  }), []);

  const historyData = [
    { id: 1, action: 'Sent Campaign', date: '2024-11-10', status: 'Completed' },
    { id: 2, action: 'Edited Template', date: '2024-11-09', status: 'Pending' },
    { id: 3, action: 'Viewed Campaign Report', date: '2024-11-08', status: 'Completed' },
    { id: 4, action: 'Added New Template', date: '2024-11-07', status: 'Completed' },
    { id: 5, action: 'Deleted Template', date: '2024-11-06', status: 'Completed' },
  ];

  const cardData = [
    { title: 'Balance', value: balance != null ? String(balance) : '—', trend: 'Account balance', chartType: 'line' },
    { title: "Today's Users", value: '2,300', trend: '+3% than last month', chartType: 'doughnut' },
    { title: 'Revenue', value: '34k', trend: '+1% than yesterday', chartType: 'doughnut' },
    { title: 'Followers', value: '+91', trend: 'Just updated', chartType: 'line' },
  ];

  return (
    <div className="dashboard-wrap">
      {/* Account overview */}
      <div className="dashboard-info-bar">
        {loadingInfo ? (
          <p className="dashboard-info-loading">Loading account info...</p>
        ) : myInfo?.userInfo ? (
          <>
            <div className="dashboard-info-header">
              <span className="dashboard-info-title">Account overview</span>
            </div>
            <div className="dashboard-info-grid">
              <div className="dashboard-info-item">
                <span className="dashboard-info-icon"><FontAwesomeIcon icon={faGaugeHigh} /></span>
                <div className="dashboard-info-meta">
                  <span className="dashboard-info-label">Limit</span>
                  <span className="dashboard-info-value">{myInfo.userInfo.quality_limit}</span>
                </div>
              </div>
              <div className="dashboard-info-item">
                <span className="dashboard-info-icon"><FontAwesomeIcon icon={faCircleCheck} /></span>
                <div className="dashboard-info-meta">
                  <span className="dashboard-info-label">Status</span>
                  <span className="dashboard-info-value dashboard-info-value--status">{myInfo.userInfo.quality_status}</span>
                </div>
              </div>
              <div className="dashboard-info-item dashboard-info-item--highlight">
                <span className="dashboard-info-icon"><FontAwesomeIcon icon={faPhone} /></span>
                <div className="dashboard-info-meta">
                  <span className="dashboard-info-label">Phone number</span>
                  <span className="dashboard-info-value">{myInfo.userInfo.phone_number}</span>
                </div>
              </div>
              <div className="dashboard-info-item">
                <span className="dashboard-info-icon"><FontAwesomeIcon icon={faIdCard} /></span>
                <div className="dashboard-info-meta">
                  <span className="dashboard-info-label">WABA ID</span>
                  <span className="dashboard-info-value">{myInfo.userInfo.waba_id}</span>
                </div>
              </div>
              <div className="dashboard-info-item">
                <span className="dashboard-info-icon"><FontAwesomeIcon icon={faHashtag} /></span>
                <div className="dashboard-info-meta">
                  <span className="dashboard-info-label">Number ID</span>
                  <span className="dashboard-info-value">{myInfo.userInfo.number_id}</span>
                </div>
              </div>
            </div>
            <div className="dashboard-info-support">
              <FontAwesomeIcon icon={faHeadset} className="dashboard-info-support-icon" />
              <span>Need help?</span>
              <a href="tel:+919845504930" className="dashboard-info-support-link">+91 98455 04930</a>
            </div>
          </>
        ) : (
          <p className="dashboard-info-error">{error || 'Account info unavailable'}</p>
        )}
      </div>

      {/* Four stat cards */}
      <div className="dashboard-cards-row">
        {cardData.map((card, index) => (
          <StatCard
            key={card.title + index}
            title={card.title}
            value={card.value}
            trend={card.trend}
            chartType={card.chartType}
          />
        ))}
      </div>

      {/* Line + Doughnut row */}
      <div className="dashboard-charts-row">
        <div className="dashboard-chart-card dashboard-chart-line">
          <h3>Revenue trend</h3>
          <div className="dashboard-chart-inner">
            <Line data={lineChartData} options={{ ...chartOptions, scales: { x: { grid: { display: false } }, y: { grid: { display: false } } } }} />
          </div>
        </div>
        <div className="dashboard-chart-card dashboard-chart-doughnut">
          <h3>Progress</h3>
          <div className="dashboard-chart-inner">
            <Doughnut data={doughnutData} options={{ ...chartOptions, cutout: '70%' }} />
          </div>
        </div>
      </div>

      {/* Templates Data + History grid */}
      <div className="dashboard-grid-row">
        <div className="dashboard-templates-card">
          <div className="dashboard-templates-header">
            <h3>Templates Data</h3>
            <div className="dashboard-templates-filters">
              {['Overall', 'Last 30 Days', 'Last 7 Days'].map((f) => (
                <button
                  key={f}
                  type="button"
                  className={templateFilter === f ? 'dashboard-filter-btn active' : 'dashboard-filter-btn'}
                  onClick={() => setTemplateFilter(f)}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
          {loadingCounts ? (
            <p className="dashboard-loading">Loading...</p>
          ) : filteredTemplateCounts.length > 0 ? (
            <div className="dashboard-templates-grid">
              {filteredTemplateCounts.map((card, index) => (
                <div key={index} className="dashboard-template-card">
                  <div className="dashboard-template-card-content">
                    <div className="dashboard-template-left">
                      <h4>{card.wa_template_category}</h4>
                      <span className={`dashboard-template-status ${card.wa_template_status === 'APPROVED' ? 'approved' : 'pending'}`}>
                        {card.wa_template_status}
                      </span>
                    </div>
                    <div className="dashboard-template-right">
                      <span className="dashboard-template-count">{card.count}</span>
                      <span>Audience</span>
                    </div>
                  </div>
                  <div className="dashboard-template-footer">{card.time_range}</div>
                </div>
              ))}
            </div>
          ) : (
            <p className="dashboard-no-data">No template data available</p>
          )}
        </div>
        <div className="dashboard-history-card">
          <h3>History</h3>
          <table className="dashboard-history-table">
            <thead>
              <tr>
                <th>Action</th>
                <th>Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {historyData.map((item) => (
                <tr key={item.id}>
                  <td>{item.action}</td>
                  <td>{item.date}</td>
                  <td className={`dashboard-history-status ${item.status.toLowerCase()}`}>{item.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bottom charts: Monthly Revenue + Users Growth */}
      <div className="dashboard-bottom-charts">
        <div className="dashboard-bottom-chart">
          <h3>Monthly Revenue</h3>
          <div className="dashboard-bottom-chart-inner">
            <Bar data={barChartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }} />
          </div>
        </div>
        <div className="dashboard-bottom-chart">
          <h3>Users Growth</h3>
          <div className="dashboard-bottom-chart-inner">
            <Line data={usersLineData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
